import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { TrendingUp } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";

const ROICalculator = () => {
  const [revenue, setRevenue] = useState<string>("");
  const [cost, setCost] = useState<string>("");
  const [result, setResult] = useState<{ roi: number; profit: number } | null>(null);

  const calculateROI = () => {
    const revenueNum = parseFloat(revenue) || 0;
    const costNum = parseFloat(cost) || 0;
    
    if (costNum === 0) {
      setResult(null);
      return;
    }

    const profit = revenueNum - costNum;
    const roi = (profit / costNum) * 100;
    
    setResult({ roi, profit });
  };

  const reset = () => {
    setRevenue("");
    setCost("");
    setResult(null);
  };

  const relatedTools = getRelatedTools("roi-calculator");

  return (
    <ToolPageLayout
      title="ROI Calculator"
      description="Calculate your Return on Investment to measure the profitability of your marketing campaigns and understand the true financial impact of your spend."
      metaDescription="ROI (Return on Investment) measures the gain or loss generated on an investment relative to its cost. Marketers use ROI to evaluate campaign profitability, compare channel performance, and justify marketing budgets to stakeholders."
      icon={TrendingUp}
      slug="roi-calculator"
      formula={{
        equation: "ROI = ((Revenue - Cost) / Cost) × 100",
        variables: [
          { name: "Revenue", description: "Total revenue generated from the investment" },
          { name: "Cost", description: "Total cost of the investment including all expenses" },
        ]
      }}
      example={{
        inputs: [
          { label: "Revenue", value: "$10,000" },
          { label: "Cost", value: "$4,000" },
        ],
        output: "150% ROI",
        interpretation: "For every $1 spent, you earned $1.50 in profit. Your investment more than paid for itself."
      }}
      improvements={[
        "Track all costs accurately including hidden fees, labor, and overhead",
        "Measure ROI across different time periods to identify trends",
        "Compare ROI across channels to allocate budget to highest performers",
        "Implement proper attribution to credit conversions correctly",
        "Focus on improving conversion rates to increase revenue per visitor",
        "Optimize ad targeting to reduce wasted spend",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "Analytics & Attribution", href: "/analytics" }}
    >
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Calculate ROI</CardTitle>
          <CardDescription>Enter your revenue and costs</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="revenue">Total Revenue ($)</Label>
            <Input
              id="revenue"
              type="number"
              placeholder="10000"
              value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cost">Total Cost ($)</Label>
            <Input
              id="cost"
              type="number"
              placeholder="4000"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={calculateROI} className="flex-1">
              Calculate ROI
            </Button>
            <Button variant="outline" onClick={reset}>
              Reset
            </Button>
          </div>

          {result && (
            <div className="mt-6 p-6 bg-primary/5 rounded-lg border border-primary/20">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Your ROI</p>
                <p className={`text-4xl font-bold ${result.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {result.roi.toFixed(2)}%
                </p>
                <p className="text-sm text-muted-foreground mt-4">Net Profit</p>
                <p className={`text-2xl font-semibold ${result.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${result.profit.toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolPageLayout>
  );
};

export default ROICalculator;
